import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { env } from '../../environments/env';
import { getE2eBypassConfig } from './e2e-bypass';

/**
 * HTTP interceptor used INSTEAD of MsalInterceptor when the e2e auth bypass is
 * active. It attaches the shared-secret / synthetic-principal headers expected
 * by the backend `e2e` Spring profile to every request that targets the API.
 *
 * See docs/e2e-playwright-backendspec.md for the header contract.
 */
@Injectable()
export class E2eAuthInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const config = getE2eBypassConfig();
    if (!config || !this.isApiRequest(req.url)) {
      return next.handle(req);
    }

    const headers: Record<string, string> = {
      'X-E2E-Secret': config.secret,
      'X-E2E-Role': config.role,
    };
    if (config.user) {
      headers['X-E2E-User'] = config.user;
    }
    if (config.scenarioId) {
      headers['X-E2E-Scenario-Id'] = config.scenarioId;
    }

    return next.handle(req.clone({ setHeaders: headers }));
  }

  private isApiRequest(url: string): boolean {
    // Match by parsed origin + path, never by naive substring/prefix checks:
    // `url.includes('/api/')` would leak the secret to any third-party URL and
    // `url.startsWith(apiUrl)` is vulnerable to prefix confusion
    // (e.g. https://api.example.com.attacker.example matching https://api.example.com).
    let requestUrl: URL;
    let apiUrl: URL;
    try {
      requestUrl = new URL(url, window.location.origin);
      apiUrl = new URL(env.get().api.url, window.location.origin);
    } catch {
      return false;
    }

    if (requestUrl.origin !== apiUrl.origin) {
      return false;
    }

    const apiPath = apiUrl.pathname.replace(/\/$/, '');
    return requestUrl.pathname === apiPath || requestUrl.pathname.startsWith(apiPath + '/');
  }
}
